import { MeetingFormValues } from "@/pages/workflows/edit/actions/meeting/workflow-meeting.schema.ts";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  ListItem,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface MeetingExternalEmailsFieldProps {
  control: Control<MeetingFormValues>;
  error?: string;
}

export const MeetingExternalEmailsField = ({ control, error }: MeetingExternalEmailsFieldProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string>();

  return (
    <Controller
      control={control}
      name="external_attendee_emails"
      render={({ field: { value, onChange } }) => {
        const add = () => {
          const email = draft.trim();
          if (!email) return;
          if (!z.email().safeParse(email).success) {
            setDraftError(t("errors:field_email_required"));
            return;
          }
          if (!value.includes(email)) onChange([...value, email]);
          setDraft("");
          setDraftError(undefined);
        };

        return (
          <Stack gap={"sm"}>
            <Title header={t("workflow:action_type_meeting_external_emails")} size={"xs"} />
            <Input
              label={t("workflow:action_type_meeting_external_emails_description")}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (draftError) setDraftError(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              error={draftError ?? error}
            />
            <Stack direction={"horizontal"}>
              <Button
                variant={"secondary"}
                size={"sm"}
                onClick={add}
                disabled={!draft.trim()}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
              >
                {t("common:add")}
              </Button>
            </Stack>
            {value.length > 0 && (
              <Stack className={"py-2"}>
                {value.map((email) => (
                  <ListItem
                    key={email}
                    forceShowAvatar
                    title={email}
                    valueSlot={
                      <DropdownMenu>
                        <DropdownMenuItem
                          title={t("calendar:remove_guest")}
                          onClick={() => onChange(value.filter((item) => item !== email))}
                        />
                      </DropdownMenu>
                    }
                  />
                ))}
              </Stack>
            )}
          </Stack>
        );
      }}
    />
  );
};
